<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import { getProductionDailyOutput, listProductionOrders } from '../productService'
import type { ProductionDailyOutputRow, ProductionOrder } from '../types'
import { buildDailyOutputCsv, buildProductionQueueCsv, evaluateProductionScheduleRisk, filterDailyOutputRows, sortProductionOrdersByPriority, summarizeDailyOutput, summarizeProductionScheduleRisks } from '../productionReporting'

const client = useApiClient()
const { t } = useI18n()
const rows = ref<ProductionOrder[]>([])
const meta = reactive({ page: 1, per_page: 20, total: 0, total_pages: 1 })
const search = ref('')
const status = ref('')
const riskFilter = ref<'all' | 'critical' | 'warning' | 'on_track'>('all')
const loading = ref(false)
const error = ref('')
const exporting = ref(false)
const exportMessage = ref('')
const reportDate = ref(new Date().toLocaleDateString('en-CA'))
const dailyRows = ref<ProductionDailyOutputRow[]>([])
const dailyLoading = ref(false)
const dailyError = ref('')
const dailyStageFilter = ref('')
const dailyEmployeeFilter = ref('')
const dailyExportMessage = ref('')
let timer: ReturnType<typeof setTimeout>

const reportingLabels = computed(() => ({
  unassigned: t('engineering.productionOrder.unassigned'),
  allStages: t('engineering.productionOrder.allStages'),
  allEmployees: t('engineering.productionOrder.allEmployees'),
  productionOutputTitle: t('engineering.productionOrder.dailyOutputTitle'),
  csvDate: t('engineering.productionOrder.date'),
  csvFilters: t('engineering.productionOrder.csvFilters'),
  csvRows: t('engineering.productionOrder.rows'),
  csvCompleted: t('engineering.productionOrder.completed'),
  csvRejected: t('engineering.productionOrder.rejected'),
  csvRework: t('engineering.productionOrder.rework'),
  csvSequence: t('engineering.productionOrder.sequence'),
  csvStage: t('engineering.productionOrder.stage'),
  csvStageCode: t('engineering.productionOrder.csvStageCode'),
  csvEmployee: t('engineering.productionOrder.employee'),
  csvEmployeeId: t('engineering.productionOrder.csvEmployeeId'),
  queueShiftSummary: t('engineering.productionOrder.queueShiftSummary'),
  csvSnapshot: t('engineering.productionOrder.snapshot'),
  queueOrders: t('engineering.productionOrder.queueOrders'),
  queueTotalPlanned: t('engineering.productionOrder.queueTotalPlanned'),
  queueTotalProduced: t('engineering.productionOrder.queueTotalProduced'),
  queueTotalRemaining: t('engineering.productionOrder.queueTotalRemaining'),
  queueOverallCompletionPercent: t('engineering.productionOrder.queueOverallCompletionPercent'),
  queueMoCode: t('engineering.productionOrder.moCode'),
  csvProduct: t('engineering.productionOrder.product'),
  queueDepartment: t('engineering.productionOrder.department'),
  csvStatus: t('engineering.productionOrder.status'),
  queueScheduleRisk: t('engineering.productionOrder.scheduleRisk'),
  csvPlanned: t('engineering.productionOrder.plan'),
  csvProduced: t('engineering.productionOrder.actual'),
  csvRemaining: t('engineering.productionOrder.remaining'),
  csvCompletionPercent: t('engineering.productionOrder.csvCompletionPercent'),
  queueStartDate: t('engineering.productionOrder.queueStartDate'),
  queueSourceWarehouse: t('engineering.productionOrder.sourceWarehouse'),
  queueDestinationWarehouse: t('engineering.productionOrder.destinationWarehouse'),
  queueEquipment: t('engineering.productionOrder.queueEquipment'),
  queueSubmittedBy: t('engineering.productionOrder.queueSubmittedBy'),
  queueNotes: t('engineering.productionOrder.notes'),
  riskStartOverdue: (days: number) => t(days === 1 ? 'engineering.productionOrder.riskStartOverdueOne' : 'engineering.productionOrder.riskStartOverdueMany', { days }),
  riskIncompleteAfterStart: (days: number) => t(days === 1 ? 'engineering.productionOrder.riskIncompleteAfterStartOne' : 'engineering.productionOrder.riskIncompleteAfterStartMany', { days }),
}))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders(client, { search: search.value || undefined, status: status.value || undefined, page: meta.page, per_page: meta.per_page })
    Object.assign(meta, result)
    rows.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch([search, status], () => {
  clearTimeout(timer)
  meta.page = 1
  timer = setTimeout(load, 300)
})

async function loadDailyOutput() {
  dailyLoading.value = true
  dailyError.value = ''
  try {
    const result = await getProductionDailyOutput(client, reportDate.value)
    dailyRows.value = result.rows
    dailyStageFilter.value = ''
    dailyEmployeeFilter.value = ''
    dailyExportMessage.value = ''
  } catch (cause) {
    dailyError.value = formatApiError(cause)
  } finally {
    dailyLoading.value = false
  }
}

watch(reportDate, loadDailyOutput)
onMounted(() => {
  void load()
  void loadDailyOutput()
})

function scheduleRisk(order: ProductionOrder) {
  return evaluateProductionScheduleRisk(order, new Date(), reportingLabels.value)
}

const riskSummary = computed(() => summarizeProductionScheduleRisks(rows.value))
const dailyStageOptions = computed(() => [...new Map(
  dailyRows.value.map((row) => [row.stage_code, { code: row.stage_code, name: row.stage_name }]),
).values()].sort((left, right) => left.name.localeCompare(right.name)))
const dailyEmployeeOptions = computed(() => [...new Map(
  dailyRows.value.map((row) => [
    String(row.employee_id ?? 'unassigned'),
    { id: String(row.employee_id ?? 'unassigned'), name: row.employee_name || t('engineering.productionOrder.unassigned') },
  ]),
).values()].sort((left, right) => left.name.localeCompare(right.name)))
const visibleDailyRows = computed(() =>
  filterDailyOutputRows(dailyRows.value, dailyStageFilter.value, dailyEmployeeFilter.value),
)
const dailySummary = computed(() => summarizeDailyOutput(visibleDailyRows.value))
const dailyFilterDescription = computed(() => {
  const stage = dailyStageOptions.value.find((option) => option.code === dailyStageFilter.value)?.name || t('engineering.productionOrder.allStages')
  const employee = dailyEmployeeOptions.value.find((option) => option.id === dailyEmployeeFilter.value)?.name || t('engineering.productionOrder.allEmployees')
  return t('engineering.productionOrder.filterStageEmployee', { stage, employee })
})
const visibleRows = computed(() => {
  const filtered = riskFilter.value === 'all'
    ? rows.value
    : riskFilter.value === 'on_track'
      ? rows.value.filter((order) => !scheduleRisk(order))
      : rows.value.filter((order) => scheduleRisk(order)?.level === riskFilter.value)
  return sortProductionOrdersByPriority(filtered)
})
const exportFilterDescription = computed(() => {
  const statusLabel = status.value ? t(`engineering.state.${status.value}`) : t('engineering.productionOrder.defaultExportStatuses')
  const searchLabel = search.value.trim() ? t('engineering.productionOrder.filterSearchPart', { value: search.value.trim() }) : ''
  const riskLabel = riskFilter.value === 'all' ? '' : t('engineering.productionOrder.filterRiskPart', { value: t(`engineering.productionOrder.${riskFilter.value === 'on_track' ? 'onTrack' : riskFilter.value}`) })
  return t('engineering.productionOrder.filterSearchRisk', { status: statusLabel, search: searchLabel, risk: riskLabel })
})

async function loadAllForStatus(exportStatus: string) {
  const collected: ProductionOrder[] = []
  let page = 1
  let totalPages = 1
  do {
    const result = await listProductionOrders(client, {
      search: search.value || undefined,
      status: exportStatus,
      page,
      per_page: 100,
    })
    collected.push(...result.items)
    totalPages = Math.max(1, result.total_pages)
    page += 1
  } while (page <= totalPages)
  return collected
}

async function exportQueue() {
  exporting.value = true
  exportMessage.value = ''
  try {
    const statuses = status.value ? [status.value] : ['planned', 'in_progress']
    const batches = await Promise.all(statuses.map(loadAllForStatus))
    let exportRows = [...new Map(batches.flat().map((row) => [row.id, row])).values()]
    if (riskFilter.value === 'on_track') exportRows = exportRows.filter((row) => !scheduleRisk(row))
    else if (riskFilter.value !== 'all') exportRows = exportRows.filter((row) => scheduleRisk(row)?.level === riskFilter.value)
    exportRows = sortProductionOrdersByPriority(exportRows)
    if (!exportRows.length) {
      exportMessage.value = t('engineering.productionOrder.queueExportEmpty')
      return
    }
    const snapshot = new Date().toISOString()
    const csv = buildProductionQueueCsv(exportRows, snapshot, exportFilterDescription.value, reportingLabels.value)
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `production-shift-queue-${snapshot.slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    exportMessage.value = t('engineering.productionOrder.queueExportSuccess', { count: exportRows.length })
  } catch (cause) {
    exportMessage.value = formatApiError(cause)
  } finally {
    exporting.value = false
  }
}

function exportDailyOutput() {
  dailyExportMessage.value = ''
  if (!visibleDailyRows.value.length) {
    dailyExportMessage.value = t('engineering.productionOrder.dailyExportEmpty')
    return
  }
  const csv = buildDailyOutputCsv(
    visibleDailyRows.value,
    reportDate.value,
    dailyFilterDescription.value,
    reportingLabels.value,
  )
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `production-daily-output-${reportDate.value}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
  dailyExportMessage.value = t('engineering.productionOrder.dailyExportSuccess', { count: visibleDailyRows.value.length })
}
</script>

<template>
  <header>
    <div><p class="eyebrow">{{ t('engineering.productionOrder.productionEyebrow') }}</p><h2>{{ t('engineering.productionOrder.ordersTitle') }}</h2><p>{{ t('engineering.productionOrder.ordersDescription') }}</p></div>
    <div class="header-actions">
      <RouterLink class="report-link new-order-link" to="/production-orders/new">{{ t('engineering.simpleOrder.newOrder') }}</RouterLink>
      <RouterLink class="report-link" to="/production-reports/daily-output">{{ t('engineering.productionOrder.dailyReportLink') }}</RouterLink>
      <button class="export" type="button" :disabled="exporting" @click="exportQueue">{{ exporting ? t('engineering.productionOrder.preparingExport') : t('engineering.productionOrder.exportQueue') }}</button>
    </div>
  </header>
  <p v-if="exportMessage" class="export-message" role="status">{{ exportMessage }}</p>
  <section class="risk-summary" :aria-label="t('engineering.productionOrder.scheduleRiskSummary')">
    <button type="button" :class="{ active: riskFilter === 'all' }" @click="riskFilter = 'all'"><span>{{ t('engineering.productionOrder.pageOrders') }}</span><strong>{{ riskSummary.all }}</strong></button>
    <button type="button" class="critical" :class="{ active: riskFilter === 'critical' }" @click="riskFilter = 'critical'"><span>{{ t('engineering.productionOrder.startOverdue') }}</span><strong>{{ riskSummary.critical }}</strong></button>
    <button type="button" class="warning" :class="{ active: riskFilter === 'warning' }" @click="riskFilter = 'warning'"><span>{{ t('engineering.productionOrder.incomplete') }}</span><strong>{{ riskSummary.warning }}</strong></button>
    <button type="button" class="track" :class="{ active: riskFilter === 'on_track' }" @click="riskFilter = 'on_track'"><span>{{ t('engineering.productionOrder.onTrack') }}</span><strong>{{ riskSummary.onTrack }}</strong></button>
  </section>
  <section class="daily-output">
    <div class="snapshot-heading">
      <div><p class="eyebrow">{{ t('engineering.productionOrder.stageDeltas') }}</p><h3>{{ t('engineering.productionOrder.dailyOutputTitle') }}</h3></div>
      <label>{{ t('engineering.productionOrder.reportDate') }}<input v-model="reportDate" type="date"></label>
    </div>
    <div v-if="dailyLoading" class="report-state">{{ t('engineering.productionOrder.loadingDailyOutput') }}</div>
    <div v-else-if="dailyError" class="report-state error">{{ dailyError }} <button type="button" @click="loadDailyOutput">{{ t('engineering.productionOrder.retry') }}</button></div>
    <div v-else-if="!dailyRows.length" class="report-state">{{ t('engineering.productionOrder.noDailyOutputForDate') }}</div>
    <template v-else>
      <div class="report-filters">
        <label>{{ t('engineering.productionOrder.stage') }}<select v-model="dailyStageFilter"><option value="">{{ t('engineering.productionOrder.allStages') }}</option><option v-for="option in dailyStageOptions" :key="option.code" :value="option.code">{{ option.name }}</option></select></label>
        <label>{{ t('engineering.productionOrder.employee') }}<select v-model="dailyEmployeeFilter"><option value="">{{ t('engineering.productionOrder.allEmployees') }}</option><option v-for="option in dailyEmployeeOptions" :key="option.id" :value="option.id">{{ option.name }}</option></select></label>
        <button class="export" type="button" :disabled="!visibleDailyRows.length" @click="exportDailyOutput">{{ t('engineering.productionOrder.exportDailyCsv') }}</button>
      </div>
      <p v-if="dailyExportMessage" class="inline-message" role="status">{{ dailyExportMessage }}</p>
      <div class="output-summary">
        <article><span>{{ t('engineering.productionOrder.completed') }}</span><strong>{{ dailySummary.completed }}</strong></article>
        <article><span>{{ t('engineering.productionOrder.rejected') }}</span><strong>{{ dailySummary.rejected }}</strong></article>
        <article><span>{{ t('engineering.productionOrder.rework') }}</span><strong>{{ dailySummary.rework }}</strong></article>
      </div>
      <div v-if="!visibleDailyRows.length" class="report-state">{{ t('engineering.productionOrder.noDailyOutputForFilters') }}</div>
      <div v-else class="report-table">
        <table>
          <thead><tr><th>{{ t('engineering.productionOrder.sequence') }}</th><th>{{ t('engineering.productionOrder.stage') }}</th><th>{{ t('engineering.productionOrder.employee') }}</th><th>{{ t('engineering.productionOrder.completed') }}</th><th>{{ t('engineering.productionOrder.rejected') }}</th><th>{{ t('engineering.productionOrder.rework') }}</th></tr></thead>
          <tbody>
            <tr v-for="(row, index) in visibleDailyRows" :key="`${row.stage_code}-${row.employee_id ?? 'unassigned'}-${index}`">
              <td>{{ row.operation_sequence }}</td>
              <td><strong>{{ row.stage_name }}</strong><small>{{ row.stage_code }}</small></td>
              <td>{{ row.employee_name || t('engineering.productionOrder.unassigned') }}</td>
              <td>{{ row.completed_quantity }}</td>
              <td>{{ row.rejected_quantity }}</td>
              <td>{{ row.rework_quantity }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
  <section class="toolbar">
    <input v-model="search" type="search" :placeholder="t('engineering.productionOrder.searchMoOrProduct')">
    <select v-model="status">
      <option value="">{{ t('engineering.productionOrder.allStatuses') }}</option>
      <option value="planned">{{ t('engineering.state.planned') }}</option>
      <option value="in_progress">{{ t('engineering.state.in_progress') }}</option>
      <option value="completed">{{ t('engineering.state.completed') }}</option>
      <option value="cancelled">{{ t('engineering.state.cancelled') }}</option>
    </select>
    <span class="sort-note">{{ t('engineering.productionOrder.sortPriority') }}</span>
  </section>
  <div v-if="loading" class="state">{{ t('engineering.productionOrder.loadingOrders') }}</div>
  <div v-else-if="error" class="state error">{{ error }} <button @click="load">{{ t('engineering.productionOrder.retry') }}</button></div>
  <div v-else-if="!rows.length" class="state">{{ t('engineering.productionOrder.noOrders') }}</div>
  <div v-else-if="!visibleRows.length" class="state">{{ t('engineering.productionOrder.noOrdersForRisk') }}</div>
  <section v-else>
    <table>
      <thead><tr><th>{{ t('engineering.productionOrder.moCode') }}</th><th>{{ t('engineering.productionOrder.product') }}</th><th>{{ t('engineering.productionOrder.department') }}</th><th>{{ t('engineering.productionOrder.status') }}</th><th>{{ t('engineering.productionOrder.scheduleRisk') }}</th><th>{{ t('engineering.productionOrder.progress') }}</th><th>{{ t('engineering.productionOrder.start') }}</th></tr></thead>
      <tbody><tr v-for="row in visibleRows" :key="row.id" :class="{ 'risk-row': scheduleRisk(row) }">
        <td><RouterLink :to="`/production-orders/${row.id}`">{{ row.mo_code }}</RouterLink></td>
        <td>{{ row.product_name || t('engineering.productionOrder.productNumber', { id: row.product_id }) }}</td>
        <td>{{ row.department_name || `#${row.department_agent_id}` }}</td>
        <td><span class="status">{{ t(`engineering.state.${row.status}`) }}</span></td>
        <td><span v-if="scheduleRisk(row)" class="risk" :class="scheduleRisk(row)?.level">{{ scheduleRisk(row)?.label }}</span><span v-else class="on-track">{{ t('engineering.productionOrder.onTrack') }}</span></td>
        <td>{{ row.produced_quantity || 0 }} / {{ row.planned_quantity }}</td>
        <td>{{ row.start_date ? formatDisplayDate(row.start_date) : '—' }}</td>
      </tr></tbody>
    </table>
    <PaginationControls :page="meta.page" :page-size="meta.per_page" :total="meta.total" @update:page="meta.page = $event; load()" @update:page-size="meta.per_page = $event; meta.page = 1; load()" />
  </section>
</template>

<style scoped>
.daily-output{margin-bottom:12px}.snapshot-heading label,.report-filters label{display:grid;gap:4px;color:#53625d;font-size:12px;font-weight:700}.report-filters{display:grid;grid-template-columns:minmax(160px,1fr) minmax(160px,1fr) auto;align-items:end;gap:9px;margin-top:12px}.report-filters .export{height:36px}.inline-message{margin:10px 0 0;color:#236451;font-size:13px;font-weight:700}.report-state{display:grid;min-height:90px;place-items:center;margin-top:12px;background:#f8faf9}.output-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;margin-top:12px}.output-summary article{display:flex;align-items:center;justify-content:space-between;padding:12px;border:1px solid #e1e7e5;background:#f8faf9}.output-summary span{color:#66726e;font-size:12px;font-weight:700}.output-summary strong{font-size:20px}.report-table{margin-top:12px;overflow-x:auto}.report-table td:nth-last-child(-n+3){font-variant-numeric:tabular-nums}.report-table td strong,.report-table td small{display:block}.report-table td small{margin-top:2px;color:#66726e}
header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}.header-actions{display:flex;gap:8px}.report-link,.export{padding:10px 14px;border:0;color:#fff;background:#07996f;font-weight:700;cursor:pointer;text-decoration:none}.report-link{background:#3568a8}.report-link.new-order-link{background:#c45a0a}.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800;text-transform:uppercase}h2{margin:4px 0}header p:last-child{color:#66726e}.export:disabled{cursor:not-allowed;opacity:.5}.export-message{margin:0 0 12px;padding:9px 12px;color:#236451;background:#eaf7f2;font-weight:700}.risk-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:0 0 12px;padding:0;border:0;background:transparent}.risk-summary button{display:flex;align-items:center;justify-content:space-between;padding:12px;border:1px solid #d7dfdc;background:#fff;color:#53625d;cursor:pointer;text-align:left}.risk-summary button strong{font-size:20px;color:#14243a}.risk-summary button.active{outline:2px solid #3568a8;outline-offset:-2px}.risk-summary .critical{border-color:#efb5ad}.risk-summary .warning{border-color:#ecd28a}.risk-summary .track{border-color:#a9d9c9}.department-output{margin-bottom:12px}.snapshot-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}.snapshot-heading h3{margin:3px 0}.snapshot-heading small{max-width:420px;color:#66726e}.department-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:9px;margin-top:12px}.department-grid article{display:grid;gap:8px;padding:12px;border:1px solid #e1e7e5;background:#f8faf9}.department-grid article>div{display:flex;justify-content:space-between;gap:8px}.department-grid span,.department-grid small{color:#66726e;font-size:12px}.department-grid progress{width:100%}.toolbar,section{padding:18px;border:1px solid #d7dfdc;background:#fff}.toolbar{display:flex;align-items:center;gap:10px;margin-bottom:14px}.toolbar input{flex:1}.sort-note{color:#66726e;font-size:12px;font-weight:700;white-space:nowrap}input,select{padding:9px 10px;border:1px solid #b9c5c1}.state{display:grid;min-height:220px;place-items:center;background:#fff}.error{color:#852d22}table{width:100%;border-collapse:collapse}th,td{padding:11px;border-bottom:1px solid #e1e7e5;text-align:left}th{color:#53625d;font-size:12px;text-transform:uppercase}.risk-row{background:#fffcf2}a{color:#285b9a;font-weight:700}.status,.risk{display:inline-block;padding:5px 8px;font-size:12px;font-weight:700}.status{color:#236451;background:#eff8f5;text-transform:capitalize}.risk.warning{color:#765213;background:#fff3c9}.risk.critical{color:#852d22;background:#ffe1dc}.on-track{color:#53625d;font-size:12px}@media(max-width:700px){header,.snapshot-heading{align-items:stretch;flex-direction:column}.header-actions{flex-wrap:wrap}.export,.report-link{align-self:flex-start}.risk-summary{grid-template-columns:repeat(2,1fr)}.report-filters,.output-summary{grid-template-columns:1fr}section{overflow-x:auto}.toolbar{align-items:stretch;flex-direction:column}.sort-note{white-space:normal}}
</style>
